import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { CompanyDashboardData, CompanyDashboardRequest } from 'src/lib/fliko/models/company/company-dashboard.data';
import { EmployeePermissions, EmployeeRole } from 'src/lib/fliko/models/company/employeeRole.data';
import { SsoDomainData, SsoUserStatus } from 'src/lib/fliko/models/company/sso.data';
import { EstateDetailsData } from 'src/lib/fliko/models/estate/estate-detail.data';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { InsuranceStatus } from 'src/lib/fliko/models/estate/insurance.data';
import { ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { LocalisedType } from 'src/lib/fliko/models/translation/generic';
import { EstateAdminData, UserType } from 'src/lib/fliko/models/users/user.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { AsyncHlp } from 'src/app/shared/helpers/async.helper';
import { AttHlp } from 'src/app/shared/helpers/attachment.helper';
import { FileInStorageHlp } from 'src/app/shared/helpers/file.helper';
import { SubHlp } from 'src/app/shared/helpers/subcontractor.helper';
import { fieldsMap } from 'src/app/shared/operators/fieldsMap.operator';
import { idMap, idMapArray } from 'src/app/shared/operators/idMap.operator';
import { ReviewsService } from 'src/app/view/estate-view/services/reviews.service';
import { ContextService } from 'src/app/view/user-context/services/context.service';
import { UserService } from 'src/app/view/users/services/user.service';
import { EmployeeHelper } from '../helpers/employee.helper';
import { CompanyApiService } from './company-api.service';
import { CompanyService } from './company.service';
import { ReportType } from './models/reportType.enum';
import { EstateInsuranceVM } from './view-models/compInsurances.view-model';
import { CompanyReviewStatus, CompanyReviewVM } from './view-models/compReview.view-model';
import { ContractStatus, CompanyContractsVM, ContractsVM, CompanyContract } from './view-models/compSubcontractor.view-model';
import { EmployeeLoginsVM } from './view-models/employee-login.view-model';
import { EmployeePermVM, EmployeeVM, EstateAdminVM, PermVM } from './view-models/employee.view-model';
import { SsoWhitelistVM, SsoAccessRequestVM, SsoConnectedVM } from './view-models/sso-user.view-model';

/**
 * Main data service with ViewModel Behavior Subjects
 */
@Injectable({ providedIn: 'root' })
export class CompanyDataService implements OnDestroy {
  private companyId$ = new BehaviorSubject<string>('');
  private reportUDMap = new Map<ReportType, BehaviorSubject<number>>();

  private estates$: BehaviorSubject<EstateData[]> | null = null;
  private estateDetails$: BehaviorSubject<EstateDetailsData[]> | null = null;
  private residents$: BehaviorSubject<ResidentData[]> | null = null;

  private employeeVMs$: BehaviorSubject<EmployeeVM[]> | null = null;
  private employeeLoginsVMs$: BehaviorSubject<EmployeeLoginsVM[]> | null = null;

  private companyContracts$: BehaviorSubject<CompanyContractsVM> | null = null;

  private defaultRolesVM$: BehaviorSubject<Map<string, EmployeePermVM>> | null = null;
  private defaultRoles: BehaviorSubject<Map<string, EmployeePermissions>> | null = null;
  private estateAdmins$ = new BehaviorSubject<EstateAdminData[] | null>(null);

  private estateInsurances$ = new BehaviorSubject<EstateInsuranceVM[] | null>(null);

  private reviewVMs$: BehaviorSubject<CompanyReviewVM[]> | null = null;
  private reviewTypes$: BehaviorSubject<LocalisedType[]> | null = null;

  private companyDashboardData$ = new BehaviorSubject<CompanyDashboardData | null>(null);
  private companyDashboardRequestId = 0;

  private ssoWhitelistVMs$: BehaviorSubject<SsoWhitelistVM[]> | null = null;
  private ssoAccessRequestVMs$: BehaviorSubject<SsoAccessRequestVM[]> | null = null;
  private ssoConnectedUsersVMs$: BehaviorSubject<SsoConnectedVM[]> | null = null;
  private ssoDomains$: BehaviorSubject<SsoDomainData[]> | null = null;

  private subscription$ = new Subscription();
  private idSubscription$ = new Subscription();

  constructor(
    private companyApiService: CompanyApiService,
    private companyService: CompanyService,
    private userService: UserService,
    private reviewsService: ReviewsService,
    private ctxSrv: ContextService,
    private errorHandlingService: ErrorHandlingService,
    @Optional() @SkipSelf() thisService?: CompanyDataService
  ) {
    if (thisService) throw new Error('Multiple instances of CompanyDataService');
    this.subscription$.add(
      this.ctxSrv.getCompanyId$().subscribe((companyId) => {
        if (companyId && companyId != this.companyId$.value) {
          this.companyDashboardRequestId++;
          this.companyDashboardData$.next(null);
          this.companyId$.next(companyId);
        }
      })
    );

    Object.values(ReportType).forEach((type) => this.reportUDMap.set(type, new BehaviorSubject(0)));
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
    this.idSubscription$.unsubscribe();
  }

  public peekCompanyId(): string {
    return this.companyId$.value;
  }

  public getEstates$(): Observable<EstateData[]> {
    if (!this.estates$) {
      this.estates$ = new BehaviorSubject<EstateData[]>([]);
      this.idSubscription$.add(
        this.companyId$.subscribe((id) => {
          if (!id) return;
          this.companyApiService.getCompanyEstates(id).then((res) => {
            if (res && Array.isArray(res)) {
              this.estates$?.next(res.filter((e) => !e.hidden));
              MapHlp.get(this.reportUDMap, ReportType.ESTATES)!.next(Date.now());
            }
          });
        })
      );
    }
    return this.estates$.asObservable();
  }

  public getEstateDetails$(): Observable<EstateDetailsData[]> {
    if (!this.estateDetails$) {
      this.estateDetails$ = new BehaviorSubject<EstateDetailsData[]>([]);
      this.idSubscription$.add(
        this.companyId$.subscribe((id) => {
          if (!id) return;
          this.companyApiService.getCompanyEstateDetails(id).then((res) => {
            this.estateDetails$?.next(Array.isArray(res) ? res : []);
            MapHlp.get(this.reportUDMap, ReportType.ESTATES)!.next(Date.now());
          });
        })
      );
    }
    return this.estateDetails$.asObservable();
  }

  public getResidents$(): Observable<ResidentData[]> {
    if (!this.residents$) {
      this.residents$ = new BehaviorSubject<ResidentData[]>([]);
      this.idSubscription$.add(
        this.companyId$.subscribe((id) => {
          if (!id) return;
          this.companyApiService.getCompanyResidents(id).then((res) => this.residents$?.next(Array.isArray(res) ? res : []));
        })
      );
    }
    return this.residents$.asObservable();
  }

  public getEstateInsurancesVMs$(): Observable<EstateInsuranceVM[] | null> {
    if (!this.estateInsurances$.value) {
      this.estateInsurances$ = new BehaviorSubject<EstateInsuranceVM[] | null>([]);
      this.initEstateInsurances();
    }
    return this.estateInsurances$.asObservable();
  }

  public getCompanyContractsVMs$(): Observable<CompanyContractsVM> {
    if (!this.companyContracts$) {
      this.companyContracts$ = new BehaviorSubject<CompanyContractsVM>({ byEstate: [], bySub: [] });
      this.initSubcontractors();
    }
    return this.companyContracts$.asObservable();
  }

  public getEmployeeVMs$(): Observable<EmployeeVM[]> {
    if (!this.employeeVMs$) {
      this.employeeVMs$ = new BehaviorSubject<EmployeeVM[]>([]);
      this.initEmployees();
    }
    return this.employeeVMs$.asObservable();
  }

  public getEmployeeLoginsVMs$(): Observable<EmployeeLoginsVM[]> {
    if (!this.employeeLoginsVMs$) {
      this.employeeLoginsVMs$ = new BehaviorSubject<EmployeeLoginsVM[]>([]);
      this.initEmployees();
    }
    return this.employeeLoginsVMs$.asObservable();
  }

  public getEmployeeVM$(id: string): Observable<EmployeeVM | undefined> {
    return this.getEmployeeVMs$().pipe(map((x) => x.find((vm) => vm.id == id)));
  }

  public patchEmployee3cxNumber(userId: string, number: string | null): void {
    if (!this.employeeVMs$) return;

    this.employeeVMs$.next(
      this.employeeVMs$.value.map((employee) => (employee.id === userId ? { ...employee, threeCxNumber: number } : employee))
    );
    this.reportUDMap.get(ReportType.EMPLOYEES)!.next(Date.now());
  }

  public getDefaultRolesVM$(): Observable<Map<string, EmployeePermVM>> {
    if (!this.defaultRolesVM$) {
      this.defaultRolesVM$ = new BehaviorSubject(new Map());
      this.subscription$.add(
        this.getDefaultRoles$().subscribe((roles) => {
          const rolesVM = new Map<string, EmployeePermVM>();
          roles.forEach((perms, key) => rolesVM.set(key, this.permissionsToVM(perms)));
          this.defaultRolesVM$!.next(rolesVM);
        })
      );
    }
    return this.defaultRolesVM$.asObservable();
  }

  public getReviewVMs$(): Observable<CompanyReviewVM[]> {
    if (!this.reviewVMs$) {
      this.reviewVMs$ = new BehaviorSubject<CompanyReviewVM[]>([]);
      this.initReviews();
    }
    return this.reviewVMs$.asObservable();
  }

  public getReviewTypes$(): Observable<LocalisedType[]> {
    if (!this.reviewTypes$) {
      this.reviewTypes$ = new BehaviorSubject<LocalisedType[]>([]);
      this.idSubscription$.add(
        this.companyId$.subscribe((id) => {
          if (!id) return;
          this.companyApiService
            .getCompanyReviewSettings(id)
            .then((settings) => AsyncHlp.updateBS(this.reviewTypes$, Object.values(settings?.types ?? {})));
        })
      );
    }
    return this.reviewTypes$.asObservable();
  }

  public getLastUpdateDate$(category: ReportType): Observable<number> {
    return this.reportUDMap.get(category)!.asObservable();
  }

  public getDefaultRole$(role: EmployeeRole): Observable<EmployeePermissions> {
    return this.getDefaultRoles$().pipe(map((map) => map.get(role) ?? {}));
  }

  public getSsoDomains$(): Observable<SsoDomainData[]> {
    if (!this.ssoDomains$) {
      this.ssoDomains$ = new BehaviorSubject<SsoDomainData[]>([]);
      this.subscription$.add(
        this.companyId$
          .pipe(switchMap((id) => (id ? this.companyService.getSsoDomains$(id) : of([]))))
          .subscribe((domains) => AsyncHlp.updateBS(this.ssoDomains$, domains))
      );
    }
    return this.ssoDomains$.asObservable();
  }

  public getCompanyDashboardData$(): Observable<CompanyDashboardData | null> {
    return this.companyDashboardData$.asObservable();
  }

  public loadCompanyDashboardData(request: CompanyDashboardRequest): Promise<boolean | null> {
    const requestId = ++this.companyDashboardRequestId;

    return this.companyApiService
      .getCompanyDashboardData(request)
      .then((data) => {
        if (requestId != this.companyDashboardRequestId) return null;
        if (!data) return false;

        this.companyDashboardData$.next(data);
        return true;
      })
      .catch((error) => {
        if (requestId != this.companyDashboardRequestId) return null;
        throw error;
      });
  }

  private getDefaultRoles$(): Observable<Map<string, EmployeePermissions>> {
    if (!this.defaultRoles) {
      this.defaultRoles = new BehaviorSubject(new Map());
      this.idSubscription$.add(
        this.companyId$.subscribe((id) => {
          if (!id) return;
          this.companyApiService.getDefaultRoles(id).then((value) => {
            const roles = value;
            if (roles) {
              const rolesMap = new Map<string, EmployeePermissions>();
              for (const role in roles) rolesMap.set(role, roles[role]);
              this.defaultRoles!.next(rolesMap);
            }
          });
        })
      );
    }
    return this.defaultRoles;
  }

  // #region Init

  private initEstateInsurances(): void {
    this.idSubscription$.add(
      combineLatest([this.companyId$, this.getEstates$()]).subscribe(async ([id, estates]) => {
        if (!id) return;

        const vms: EstateInsuranceVM[] = [];

        this.companyApiService.getCompanyEstateInsurances(id).then((data) => {
          const now = Date.now();

          for (const insurances of data) {
            for (const insurance of Object.values(insurances)) {
              const files = (insurance.attachments ?? []).map((att) => FileInStorageHlp.fromPath(att)!) ?? [];
              let status = InsuranceStatus.ACTIVE;
              if (insurance.endDate < now) {
                status = InsuranceStatus.EXPIRED;
              } else if (insurance.startDate > now) {
                status = InsuranceStatus.FUTURE;
              }
              vms.push({
                id: insurance.id,
                estateId: insurance.estateId,
                estate: estates.find((e) => e.id == insurance.estateId)?.name ?? '',
                insuranceCompanyNIP: insurance.insuranceCompanyNIP,
                insuranceCompany: insurance.insuranceCompany,
                policyNo: insurance.policyNo,
                startDate: insurance.startDate,
                endDate: insurance.endDate,
                totalPremium: insurance.totalPremium,
                insuranceSum: insurance.insuranceSum,
                isOCManagement: insurance.isOCManagement,
                note: insurance.note,
                currency: insurance.currency,
                status,
                files
              });
            }
          }

          AsyncHlp.updateBS(this.estateInsurances$, vms);
          this.reportUDMap.get(ReportType.INSURANCES)!.next(Date.now());
        });
      })
    );
  }

  private initSubcontractors(): void {
    this.idSubscription$.add(
      this.companyId$
        .pipe(switchMap((id) => combineLatest([of(id), this.getEstates$()])))
        .subscribe(async ([companyId, estates]) => {
          const eMap = MapHlp.idMap(estates);
          const compEstates = estates.map((x) => x.id);
          const [subs, estateSubs, serviceUsers, estateDetails] = await Promise.all([
            this.companyApiService.getCompanySubcontractors(companyId),
            this.companyApiService.getCompanyEstateSubcontractors(companyId),
            this.companyApiService.getServiceUsers(companyId),
            this.companyApiService.getCompanyEstateDetails(companyId)
          ]);
          const detailsMap = MapHlp.idMap(estateDetails);
          const bySub: ContractsVM[] = [];
          const estContrMap = new Map<string, CompanyContract[]>();
          subs?.forEach((sub) => {
            const subEstates = sub.estates?.filter((e) => compEstates.includes(e)) ?? [];
            const eSubs = estateSubs.filter((s) => s.id == sub.id && subEstates.includes(s.estateId));
            const contracts: CompanyContract[] = [];
            const subUsers = serviceUsers.filter((u) => u.subcontractorId == sub.id);
            eSubs.forEach((eSub) => {
              const lastCon = SubHlp.getLastContract(eSub.contracts ?? []);
              const estate = eMap.get(eSub.estateId);
              const details = detailsMap.get(eSub.estateId);
              const status = eSub.isArchived
                ? ContractStatus.ARCHIVED
                : lastCon?.indef || (lastCon?.endDate ?? 0) > Date.now()
                  ? ContractStatus.ACTIVE
                  : lastCon?.startDate
                    ? ContractStatus.ENDED
                    : ContractStatus.INACTIVE;
              const base = {
                otherName: null,
                NIP: null,
                startDate: lastCon?.startDate ?? 0,
                endDate: lastCon?.indef ? -1 : (lastCon?.endDate ?? 0),
                value: lastCon?.amount ?? 0,
                currency: details?.currency ?? 'PLN',
                noticeTime: eSub.periodOfNotice,
                status,
                attachments: AttHlp.toFileInStorageData(eSub.attachments),
                types: [],
                serviceUsers: subUsers
              };
              contracts.push({
                ...base,
                id: eSub.estateId,
                name: estate?.name ?? 'ERROR',
                otherName: eSub.companyData?.name ?? null
              });
              const estContract: CompanyContract = {
                ...base,
                id: sub.id,
                name: eSub.companyData?.name ?? sub.name,
                otherName: eSub.companyData?.name ? sub.name : null,
                NIP: sub.NIP
              };
              MapHlp.arrayMapAdd(estContrMap, eSub.estateId, estContract);
            });
            if (contracts.length) {
              bySub.push({ id: sub.id, name: `[${ParserHlp.parseNipDisplay(sub.NIP)}] ${sub.name}`, contracts });
            }
          });
          const byEstate: ContractsVM[] = [];
          estContrMap.forEach((contracts, estateId) => {
            const name = eMap.get(estateId)?.name ?? 'ERROR';
            byEstate.push({ id: estateId, name, contracts });
          });
          this.companyContracts$!.next({ byEstate, bySub });
          MapHlp.get(this.reportUDMap, ReportType.SUBCONTRACTORS)!.next(Date.now());
        })
    );
  }

  private initEmployees(): void {
    this.subscription$.add(
      combineLatest([
        this.getEstates$().pipe(fieldsMap<EstateData, string>('id', 'name')),
        this.userService.getUsers$().pipe(idMap()),
        this.ctxSrv.getCompanyId$(),
        this.companyService.getCompanyEmployees$(),
        this.getEstateAdmins$().pipe(idMapArray(['userId'])) // FIXME: why empty?
      ]).subscribe(async ([eMap, usersMap, companyId, employees, adminUsersMap]) => {
        if (!companyId) return;

        const commonEmployeesMap = MapHlp.idMap(await this.companyService.getEmployees(companyId));

        const employeeVMs: EmployeeVM[] = [];
        const employeeLoginsVMs: EmployeeLoginsVM[] = [];
        if (employees.length) {
          employees.forEach((employee) => {
            const user = usersMap.get(employee.id);
            if (user) {
              const commonEmp = commonEmployeesMap.get(employee.id);
              const eAdmins: EstateAdminVM[] = [];
              const admins = adminUsersMap.get(employee.id);
              admins?.forEach((a) =>
                eAdmins.push({
                  id: a.id,
                  userId: a.userId,
                  estateId: a.estateId,
                  estateName: eMap.get(a.estateId) ?? '',
                  createDate: new Date(a.createTimestamp ?? 0)
                })
              );
              employeeVMs.push({
                id: employee.id,
                estateAdmins: eAdmins.sort((a, b) => a.estateName.localeCompare(b.estateName)),
                user,
                role: employee?.role ?? EmployeeRole.ADMIN,
                roleUpdate: employee?.lastUpdate ?? Date.now(),
                lastLogin: user.lastActivity ?? 0,
                threeCxNumber: commonEmp?.['3cxNumber'] ?? null,
                permissionsVM: this.permissionsToVM(employee?.permissions ?? {}),
                permissions: employee?.permissions ?? {}
              });

              if (user.userType != UserType.DELETED && user.userType != UserType.DEACTIVATED) {
                employeeLoginsVMs.push({
                  id: employee.id,
                  user,
                  role: employee?.role ?? EmployeeRole.ADMIN,
                  lastLogin: user.lastActivity ?? 0,
                  allLogins: (commonEmp?.lastLogins ?? []).sort((a, b) => a - b)
                });
              }
            }
          });
        }
        AsyncHlp.updateBS(this.employeeVMs$, employeeVMs);
        AsyncHlp.updateBS(this.employeeLoginsVMs$, employeeLoginsVMs);

        this.reportUDMap.get(ReportType.EMPLOYEES)!.next(Date.now());
      })
    );
  }

  private initReviews(): void {
    this.idSubscription$.add(
      this.companyId$
        .pipe(
          switchMap((id) =>
            combineLatest([
              of(id),
              this.getEstates$().pipe(idMap()),
              this.getReviewTypes$().pipe(idMap()),
              this.reviewsService.getContractors$().pipe(idMap())
            ])
          )
        )
        .subscribe(async ([companyId, estateMap, typesMap, contractorsMap]) => {
          const [reviews, allReports] = await Promise.all([
            this.companyApiService.getCompanyReviews(companyId),
            this.companyApiService.getCompanyReviewReports(companyId)
          ]);
          if (estateMap.size == 0) return;

          const now = Date.now();
          const reviewVMs: CompanyReviewVM[] = [];
          reviews.forEach((review) => {
            const reports = allReports.filter((rep) => rep.reviewId == review.id).sort((a, b) => b.reviewDate - a.reviewDate);
            const last = reports[0];
            const type = typesMap.get(review.type) ?? null;
            const contractor = contractorsMap.get(last?.contractorId) ?? null;

            let status = CompanyReviewStatus.PLANNED;
            if (review.deleted) status = CompanyReviewStatus.DELETED;
            else if (!last) status = CompanyReviewStatus.NO_REPORT;
            else if ((last.nextReviewDate ?? 0) < now) status = CompanyReviewStatus.UNPLANNED;

            reviewVMs.push({
              id: review.id,
              estate: estateMap.get(review.estateId)!,
              reports,
              lastReport: last,
              status,
              title: review.title,
              type,
              contractor
            });
          });

          AsyncHlp.updateBS(this.reviewVMs$, reviewVMs);
          this.reportUDMap.get(ReportType.REVIEWS)!.next(Date.now());
        })
    );
  }

  // #endregion Init

  private getEstateAdmins$(): Observable<EstateAdminData[] | null> {
    if (!this.estateAdmins$.value) {
      this.idSubscription$.add(
        this.companyId$.subscribe((id) => {
          if (!id) return;
          this.companyApiService.getCompanyEstateAdmins(id).then((response) => this.estateAdmins$!.next(response ?? []));
        })
      );
    }
    return this.estateAdmins$.asObservable();
  }

  private permissionsToVM(perms: EmployeePermissions): EmployeePermVM {
    const ret: EmployeePermVM = {};
    const all = EmployeeHelper.allPermissions;
    for (const permKey in perms) {
      const perm: PermVM = (perms as any)[permKey] ?? {};
      const allPerm: PermVM = (all as any)[permKey];
      if (allPerm) {
        ret[EmployeeHelper.employeePermModuleKey(permKey)] = {
          view: allPerm.view ? !!perm.view : null,
          edit: allPerm.edit ? !!perm.edit : null,
          report: allPerm.report ? !!perm.report : null,
          other: Object.keys(perm).filter(EmployeeHelper.filterOtherPerms) ?? []
        };
      } else {
        this.errorHandlingService.showSilentWarning(
          `Permission undefined in AllPermissions: ${permKey}: ${JSON.stringify(perm)}`
        );
      }
    }
    return ret;
  }

  /**
   *  Function used only in EmployeeAddEstate and EmployeeDetails component. Force reset list of estate admins after add/delete
   */
  public resetEstateAdmins(): void {
    this.estateAdmins$.next(null);
    // ? Init estateAdmins
    this.getEstateAdmins$();
  }

  // #region SSO User Management

  public getSsoWhitelistVMs$(): Observable<SsoWhitelistVM[]> {
    if (!this.ssoWhitelistVMs$) {
      this.ssoWhitelistVMs$ = new BehaviorSubject<SsoWhitelistVM[]>([]);
      this.subscription$.add(
        combineLatest([
          this.companyService.getSsoWhitelist$(this.peekCompanyId()),
          this.userService.getUsers$().pipe(idMap()),
          this.estates$!.pipe(idMap())
        ]).subscribe(([entries, usersMap, estatesMap]) => {
          const vms: SsoWhitelistVM[] = entries.map((e) => {
            const estates: EstateData[] = [];
            e.estateIds.forEach((eId) => {
              const estate = estatesMap.get(eId);
              if (estate && !estate.hidden) estates.push(estate);
            });
            return {
              ...e,
              estates,
              notes: e.notes ?? '',
              adminUser: usersMap.get(e.adminId) ?? null
            };
          });
          AsyncHlp.updateBS(this.ssoWhitelistVMs$, vms);
        })
      );
    }

    return this.ssoWhitelistVMs$.asObservable();
  }

  public getSsoAccessRequestVMs$(): Observable<SsoAccessRequestVM[]> {
    if (!this.ssoAccessRequestVMs$) {
      this.ssoAccessRequestVMs$ = new BehaviorSubject<SsoAccessRequestVM[]>([]);
      this.initRegistryUserVms();
    }
    return this.ssoAccessRequestVMs$.asObservable();
  }

  public getSsoConnectedUsersVMs$(): Observable<SsoConnectedVM[]> {
    if (!this.ssoConnectedUsersVMs$) {
      this.ssoConnectedUsersVMs$ = new BehaviorSubject<SsoConnectedVM[]>([]);
      this.initRegistryUserVms();
    }
    return this.ssoConnectedUsersVMs$.asObservable();
  }

  private initRegistryUserVms(): void {
    this.subscription$.add(
      combineLatest([
        this.companyId$.pipe(
          switchMap((id) =>
            id
              ? combineLatest([this.companyService.getSsoUsers$(id), this.companyService.getCompanyEmployees$().pipe(idMap())])
              : of([])
          )
        ),
        this.estates$!.pipe(idMap()),
        this.userService.getUsers$().pipe(idMap())
      ]).subscribe(async ([[ssoUsers, employees], estatesMap, usersMap]) => {
        const requestVms: SsoAccessRequestVM[] = [];
        const connectedVms: SsoConnectedVM[] = [];

        ssoUsers.forEach((user) => {
          if (user.status == SsoUserStatus.NEW) {
            requestVms.push(user);
          } else {
            const estates: EstateData[] = [];
            user.estateIds.forEach((eId) => {
              const estate = estatesMap.get(eId);
              if (estate && !estate.hidden) estates.push(estate);
            });
            connectedVms.push({
              ...user,
              employee: employees.get(user.userId) ?? null,
              estates,
              role: user.role!,
              adminUser: usersMap.get(user.adminId ?? '') ?? null
            });
          }
        });
        AsyncHlp.updateBS(this.ssoAccessRequestVMs$, requestVms);
        AsyncHlp.updateBS(this.ssoConnectedUsersVMs$, connectedVms);
      })
    );
  }

  public refreshSsoData(): void {
    // Force refresh by resetting behavior subjects
    this.ssoWhitelistVMs$ = null;
    this.ssoAccessRequestVMs$ = null;
    this.ssoConnectedUsersVMs$ = null;
  }

  // #endregion SSO User Management
}
